import { useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";
import { useVoiceAttributeLabels } from "../../hooks/useVoiceAttributeLabels";
import { VoiceFilterValue, VoiceGender, VoiceStyle } from "../../types";
import {
  VoiceAgentVoiceFilter,
  VoiceAgentVoiceFilterOption,
} from "./components/VoiceAgentVoiceFilter";

export interface VoiceAgentVoiceFiltersProps {
  gender: VoiceFilterValue<VoiceGender>;
  style: VoiceFilterValue<VoiceStyle>;
  isDisabled?: boolean;
  onGenderChange: (value: VoiceFilterValue<VoiceGender>) => void;
  onStyleChange: (value: VoiceFilterValue<VoiceStyle>) => void;
}

export const VoiceAgentVoiceFilters: React.FC<VoiceAgentVoiceFiltersProps> = ({
  gender,
  style,
  isDisabled,
  onGenderChange,
  onStyleChange,
}) => {
  const { t } = useTranslations();
  const { genderLabels, styleLabels } = useVoiceAttributeLabels();

  const genderOptions = useMemo<VoiceAgentVoiceFilterOption<VoiceGender>[]>(
    () => [
      {
        id: "all",
        label: t({
          id: "client-support-page.all-genders",
          defaultMessage: "All Genders",
        }),
      },
      { id: "female", label: genderLabels.female },
      { id: "male", label: genderLabels.male },
    ],
    [genderLabels, t]
  );

  const styleOptions = useMemo<VoiceAgentVoiceFilterOption<VoiceStyle>[]>(
    () => [
      {
        id: "all",
        label: t({
          id: "client-support-page.all-styles",
          defaultMessage: "All Styles",
        }),
      },
      { id: "bright", label: styleLabels.bright },
      { id: "firm", label: styleLabels.firm },
      { id: "easy-going", label: styleLabels["easy-going"] },
      { id: "clear", label: styleLabels.clear },
      { id: "upbeat", label: styleLabels.upbeat },
      { id: "even", label: styleLabels.even },
      { id: "friendly", label: styleLabels.friendly },
      { id: "lively", label: styleLabels.lively },
      { id: "excitable", label: styleLabels.excitable },
      { id: "breezy", label: styleLabels.breezy },
      { id: "smooth", label: styleLabels.smooth },
      { id: "gravelly", label: styleLabels.gravelly },
      { id: "soft", label: styleLabels.soft },
      { id: "mature", label: styleLabels.mature },
      { id: "casual", label: styleLabels.casual },
      { id: "knowledgeable", label: styleLabels.knowledgeable },
      { id: "informative", label: styleLabels.informative },
      { id: "youthful", label: styleLabels.youthful },
      { id: "forward", label: styleLabels.forward },
      { id: "gentle", label: styleLabels.gentle },
    ],
    [styleLabels, t]
  );

  return (
    <div className="flex min-w-0 flex-wrap gap-4 max-lg:gap-2">
      <VoiceAgentVoiceFilter
        value={gender}
        options={genderOptions}
        isDisabled={isDisabled}
        onChange={onGenderChange}
      />
      <VoiceAgentVoiceFilter
        value={style}
        options={styleOptions}
        isDisabled={isDisabled}
        onChange={onStyleChange}
      />
    </div>
  );
};
